// Replay artifact for the arcade — per-round kill log, encoded as a URL hash
// short enough for DM/Twitter sharing (~200 chars). The PATH between kills
// is interpolated at playback (catmull-rom smoothed); the meaningful artifact
// is WHERE the player killed, not every cursor wiggle. Same shape as Wordle's
// emoji grid — stylized summary of the run, not a literal recording.
//
// URL format: #arcade=v1.{cycleDecimal}.{nonceHex8}.{killsBase64Url}.{score}
// Per-kill packing: 5 bytes (uint16 tMs little-endian + uint8 x + uint8 y + uint8 combo).
// 30 kills × 5 = 150 bytes raw → ~200 base64url chars; well under any practical share limit.

export interface KillEvent {
  tMs: number;   // ms from round start (uint16 — 65s range)
  x: number;     // 0..1 normalized
  y: number;     // 0..1 normalized
  combo: number; // capped at 255 (uint8)
}

export interface Replay {
  version: 1;
  cycle: number;
  nonce: number;
  score: number;
  kills: KillEvent[];
}

const CHALLENGE_PREFIX = '#arcade=';

// === Encoding ===

export function encodeReplay(r: Replay): string {
  const buf = new Uint8Array(r.kills.length * 5);
  const view = new DataView(buf.buffer);
  for (let i = 0; i < r.kills.length; i++) {
    const k = r.kills[i]!;
    const tMs = clampInt(k.tMs, 0, 65535);
    const x = Math.round(clampFloat(k.x, 0, 1) * 255);
    const y = Math.round(clampFloat(k.y, 0, 1) * 255);
    const combo = clampInt(k.combo, 0, 255);
    view.setUint16(i * 5, tMs, true);
    view.setUint8(i * 5 + 2, x);
    view.setUint8(i * 5 + 3, y);
    view.setUint8(i * 5 + 4, combo);
  }
  const killsB64 = bytesToBase64Url(buf);
  const nonceHex = (r.nonce >>> 0).toString(16).padStart(8, '0');
  const safeCycle = clampInt(r.cycle, 0, 999999);
  const safeScore = clampInt(r.score, 0, 999999);
  return `v1.${safeCycle}.${nonceHex}.${killsB64}.${safeScore}`;
}

export function decodeReplay(payload: string): Replay | null {
  try {
    if (typeof payload !== 'string' || payload.length === 0) return null;
    const parts = payload.split('.');
    if (parts.length !== 5) return null;
    if (parts[0] !== 'v1') return null;  // future version markers must not silently parse
    const cycle = parseInt(parts[1]!, 10);
    const nonce = parseInt(parts[2]!, 16);
    const killsB64 = parts[3]!;
    const score = parseInt(parts[4]!, 10);
    if (!Number.isFinite(cycle) || !Number.isFinite(nonce) || !Number.isFinite(score)) return null;
    if (cycle < 0 || nonce < 0 || score < 0) return null;

    const buf = base64UrlToBytes(killsB64);
    if (buf === null) return null;
    if (buf.length % 5 !== 0) return null;

    const view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
    const kills: KillEvent[] = [];
    for (let i = 0; i < buf.length; i += 5) {
      kills.push({
        tMs: view.getUint16(i, true),
        x: view.getUint8(i + 2) / 255,
        y: view.getUint8(i + 3) / 255,
        combo: view.getUint8(i + 4),
      });
    }
    return { version: 1, cycle, nonce, score, kills };
  } catch {
    return null;
  }
}

// === Recorder ===

export class ReplayRecorder {
  private startMs = performance.now();
  readonly kills: KillEvent[] = [];

  recordKill(x: number, y: number, combo: number): void {
    this.kills.push({
      tMs: Math.round(performance.now() - this.startMs),
      x,
      y,
      combo,
    });
  }
}

// === Ghost SVG renderer ===
// Catmull-rom-smoothed polyline through kill points, normalized 0..1 viewBox,
// pathLength="1" + stroke-dashoffset CSS animation traces it over `durationMs`
// (the animation is in CSS, not JS — this returns the static markup).

export function renderGhostSvg(kills: KillEvent[], _durationMs = 3000): string {
  if (kills.length < 2) return '';
  const d = catmullRomPath(kills.map((k) => ({ x: k.x, y: k.y })));
  if (!d) return '';
  return `<svg class="herm-arcade-ghost" viewBox="0 0 1 1" preserveAspectRatio="none" aria-hidden="true">`
    + `<path class="herm-ghost-path" d="${d}" pathLength="1" />`
    + `</svg>`;
}

function catmullRomPath(pts: { x: number; y: number }[]): string {
  if (pts.length < 2) return '';
  const out: string[] = [`M${fixed(pts[0]!.x)} ${fixed(pts[0]!.y)}`];
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[Math.max(0, i - 1)]!;
    const p1 = pts[i]!;
    const p2 = pts[i + 1]!;
    const p3 = pts[Math.min(pts.length - 1, i + 2)]!;
    const c1x = p1.x + (p2.x - p0.x) / 6;
    const c1y = p1.y + (p2.y - p0.y) / 6;
    const c2x = p2.x - (p3.x - p1.x) / 6;
    const c2y = p2.y - (p3.y - p1.y) / 6;
    out.push(`C${fixed(c1x)} ${fixed(c1y)} ${fixed(c2x)} ${fixed(c2y)} ${fixed(p2.x)} ${fixed(p2.y)}`);
  }
  return out.join(' ');
}

function fixed(n: number): string {
  return n.toFixed(4);
}

// === URL helpers ===

export function readChallengeFromUrl(): Replay | null {
  if (typeof location === 'undefined') return null;
  const hash = location.hash;
  if (!hash.startsWith(CHALLENGE_PREFIX)) return null;
  return decodeReplay(hash.slice(CHALLENGE_PREFIX.length));
}

export function buildChallengeUrl(replay: Replay): string {
  return `${location.origin}${location.pathname}${CHALLENGE_PREFIX}${encodeReplay(replay)}`;
}

// === base64url helpers (URL-safe, no padding) ===

function bytesToBase64Url(buf: Uint8Array): string {
  let bin = '';
  for (let i = 0; i < buf.length; i++) bin += String.fromCharCode(buf[i]!);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function base64UrlToBytes(s: string): Uint8Array | null {
  if (typeof s !== 'string') return null;
  // Reject non-base64url charset early so we return null instead of throwing
  if (s.length > 0 && !/^[A-Za-z0-9_-]*$/.test(s)) return null;
  const pad = s.length % 4 === 0 ? '' : '='.repeat(4 - (s.length % 4));
  try {
    const bin = atob(s.replace(/-/g, '+').replace(/_/g, '/') + pad);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
  } catch {
    return null;
  }
}

function clampInt(v: number, lo: number, hi: number): number {
  if (!Number.isFinite(v)) return lo;
  return Math.max(lo, Math.min(hi, Math.round(v)));
}

function clampFloat(v: number, lo: number, hi: number): number {
  if (!Number.isFinite(v)) return lo;
  return Math.max(lo, Math.min(hi, v));
}
